import { ComponentChildren } from 'preact';
import { useRef, useCallback } from 'preact/hooks';
import { useId } from '../hooks/UNSAFE_useId';
import { CollapsibleHeader } from './CollapsibleHeader';
import { CollapsibleContent } from './CollapsibleContent';

type Props = {
  /**
   * Id of the component
   */
  id?: string;

  /**
   * Collapsible's header. If not specified, the header contains only an open/close icon. Note that the header text is required for JET collapsible for accessibility purposes.
   */
  header?: ComponentChildren;

  /**
   * The content node to be collapsed
   */
  children?: ComponentChildren;

  /**
   * Disables the collapsible if set to true
   */
  isDisabled?: boolean;

  /**
   * Specifies if the content is expanded
   */
  isExpanded?: boolean;

  /**
   * Changes chevron icon placement at the end of the collapsible header
   */
  iconPosition?: 'start' | 'end';

  /**
   * Controls the header display which con include a divider below the collapsible header
   */
  variant?: 'basic' | 'horizontal-rule';

  /**
   * Property that triggers a callback immediately when toggle happens and value of expanded property should be updated
   */
  onToggle?: (details: ToggleDetail) => void;

  /**
   * Property that triggers a callback after toggle animation is done
   */
  onTransitionEnd?: (details: TransitionEnd) => void;
};

type ToggleDetail = TransitionEnd & {
  target: EventTarget | null;
};

type TransitionEnd = {
  value: boolean;
};

/**
 * A collapsible displays a header that can be expanded to show its content.
 */

export const Collapsible = ({
  id,
  header,
  children,
  isDisabled = false,
  isExpanded = false,
  iconPosition = 'start',
  variant = 'basic',
  onToggle,
  onTransitionEnd
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const uniqueID = useId();
  const headerId = `oj-collapsible-header-${uniqueID}`;
  const contentId = `oj-collapsible-content-${uniqueID}`;

  /**
   * Function handling toggle and invoking callback for collapsing/expanding
   */
  const toggleHandler = useCallback(
    (target: EventTarget | null) => {
      if (isDisabled) {
        return;
      }
      onToggle?.({
        value: !isExpanded,
        target
      });
    },
    [isDisabled, onToggle, isExpanded]
  );

  /**
   * Function that is triggerd when animation ends
   */
  const transitionEndHandler = useCallback(() => {
    onTransitionEnd?.({
      value: isExpanded
    });
  }, [onTransitionEnd, isExpanded]);

  return (
    <div id={id} ref={rootRef}>
      <CollapsibleHeader
        id={headerId}
        contentId={contentId}
        toggleHandler={toggleHandler}
        isDisabled={isDisabled}
        isExpanded={isExpanded}
        iconPosition={iconPosition}
        variant={variant}>
        {header}
      </CollapsibleHeader>
      <CollapsibleContent
        id={contentId}
        isExpanded={isExpanded}
        onTransitionEnd={transitionEndHandler}>
        {children}
      </CollapsibleContent>
    </div>
  );
};
